import React, { useState, useEffect, Suspense } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { selectID } from "../../features/counter/counterSlice";
import { MsalAuthenticationTemplate, UnauthenticatedTemplate } from "@azure/msal-react";
import { apiService } from "../../api/apiAdapter";
import MySubscriptions from "./mySubscriptions";
import ManageUsers from "./manageUsers";
import MyDetails from "./myDetails";
import { updateState } from "../../features/counter/counterSlice";
import { handleClearCache } from "../../utils/index";
const NavBar = React.lazy(() => import("../../components/navBar"));
const TopicBanner = React.lazy(() => import("../../components/topicBanner"));
const Footer = React.lazy(() => import("../../components/footer"));

const UserProfile = (props) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const search = location.search;
  const [profileData, setProfileData] = useState(null);
  const [activeTab, setActiveTab] = useState("details");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const authID = useSelector(selectID);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(
      updateState({
        ditaPath: "",
        headTitle: "",
      }),
    );
  }, [dispatch]);

  const handleTabClick = (tabName) => {
    setActiveTab((prev) => (prev === tabName ? "" : tabName));
  };

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!authID?.u?.["signInNames.emailAddress"]) {
          setError("User email not available");
          setLoading(false);
          return;
        }
        console.log("Fetching profile for email:", authID);
        if (authID.u["signInNames.emailAddress"]) {
          const response = await apiService.getUserProfile(authID.u["signInNames.emailAddress"]);

          if (response.success) {
            setProfileData(response.data);
            setError(null);
            console.log("Profile data:", response.data);
          } else {
            setError(response.message || "Failed to fetch profile data");
          }
        }
      } catch (err) {
        console.error("Profile fetch error:", err);
        setError(err.message || "An error occurred while fetching profile data");
      } finally {
        setLoading(false);
      }
    };

    if (authID?.u) {
      fetchUserProfile();
    } else {
      setLoading(false);
    }
  }, [authID]);

  const primarySubscription =
    profileData?.subscriptions?.find?.((sub) =>
      ["151", "168", "170"].includes(String(sub?.tg_subscriptionid)),
    ) || null;

  const isCacheCleared = () => {
    const keys = Object.keys(localStorage);
    const allowedKeys = ["savedReferrer", "savedFavourites", "savedSearches"];

    return keys.every((key) => allowedKeys.includes(key));
  };

  return (
    <>
      <Suspense fallback={<div className="loader"></div>}>
        <NavBar push={navigate} />
        <TopicBanner pathname={pathname} search={search} push={navigate} />
        <MsalAuthenticationTemplate
          errorComponent={() => (
            <div className="error-container">
              <p>Authentication failed. Please clear your browser cache and try again.</p>
              <button
                className="cbtn btn-sm borderNone btn-calc btn-custom"
                onClick={handleClearCache}
              >
                Clear Cache & Reload
              </button>
              <p style={{ marginTop: "10px", fontWeight: "bold" }}>
                After clearing cache, please click the Login button in the top right corner.
              </p>
            </div>
          )}
        >
          {error && (
            <div className="error-container">
              <button onClick={() => window.location.reload()} className="retry-button">
                Retry
              </button>
            </div>
          )}
          {loading && (
            <div className="loader-container">
              <div className="loader"></div>
              <p>Loading profile data...</p>
            </div>
          )}
          {!error && !loading && (
            <div className="container show-my-profile" id="wrap">
              {/* Tab Navigation */}
              <div className="col-lg-8 col-sm-8 col-xs-12 tab">
                <ul className="nav nav-tabs" role="tablist" id="myTab">
                  <li
                    role="presentation"
                    className={activeTab === "details" ? "active" : ""}
                    onClick={() => handleTabClick("details")}
                  >
                    <div style={{ cursor: "pointer" }}>My details</div>
                  </li>

                  {(primarySubscription?.tg_subscriptionid === "168" ||
                    primarySubscription?.tg_subscriptionid === "151") &&
                    primarySubscription?.startdate !== "2100-01-01T00:00:01Z" &&
                    primarySubscription?.tg_expirydate !== "2100-01-01T00:00:01Z" && (
                      <li
                        role="presentation"
                        className={activeTab === "subscriptions" ? "active" : ""}
                        onClick={() => handleTabClick("subscriptions")}
                      >
                        <div style={{ cursor: "pointer" }}>My subscriptions</div>
                      </li>
                    )}

                  {primarySubscription?.tg_subscriptionid === "168" && (
                    <li
                      role="presentation"
                      className={activeTab === "manageUsers" ? "active" : ""}
                      onClick={() => handleTabClick("manageUsers")}
                    >
                      <div style={{ cursor: "pointer" }}>Manage users</div>
                    </li>
                  )}
                </ul>
              </div>

              {/* Collapsible Tab Content (Bootstrap v3) */}
              <div className="tab-content">
                {/* My Details */}
                <div
                  className={`tab-pane collapse ${activeTab === "details" ? "in" : ""}`}
                  id="details"
                  role="tabpanel"
                  // For a smoother experience, you can add transitions with .collapsing,
                  // but that typically involves Bootstrap's JS or a React collapse plugin.
                >
                  <MyDetails contactData={profileData?.contact} push={navigate} />
                </div>

                {/* My Subscriptions */}
                <div
                  className={`tab-pane collapse ${activeTab === "subscriptions" ? "in" : ""}`}
                  id="subscriptions"
                  role="tabpanel"
                >
                  <MySubscriptions
                    contactId={profileData?.contact?.contactid}
                    isChild={!!profileData?.contact?._tg_parentcontactid_value}
                    subscriptions={profileData?.subscriptions || []}
                    push={navigate}
                  />
                </div>

                {/* Manage Users */}
                <div
                  className={`tab-pane collapse ${activeTab === "manageUsers" ? "in" : ""}`}
                  id="manageUsers"
                  role="tabpanel"
                >
                  <ManageUsers
                    subscriptions={profileData?.subscriptions || []}
                    mainAccountContact={profileData?.contact}
                  />
                </div>
              </div>
            </div>
          )}
        </MsalAuthenticationTemplate>
        <UnauthenticatedTemplate>
          <div className="container" id="wrap">
            {isCacheCleared() ? (
              <>
                <h3>
                  <strong>Cache cleared successfully!</strong>
                </h3>
                <div
                  style={{
                    padding: "15px",
                    backgroundColor: "#d4edda",
                    border: "1px solid #c3e6cb",
                    borderRadius: "4px",
                    marginTop: "10px",
                  }}
                >
                  Please click the <strong>Login</strong> button in the top right corner to
                  continue.
                </div>
              </>
            ) : (
              <>
                <h3>
                  <strong>Content not displaying</strong>
                </h3>
                <p>
                  Clearing your browser or application's cache can fix display issues and improve
                  performance. If problems persist, please contact IT Support at{" "}
                  <a href="mailto:support@tg.org.au">support@tg.org.au</a>.
                </p>
                <br />
                <button
                  onClick={handleClearCache}
                  className="cbtn btn-sm borderNone btn-calc btn-custom"
                >
                  Clear Cache & Retry
                </button>
                <br />
                <br />
                <div className="help-guides">
                  <h4>Helpful how-to guides:</h4>
                  <ul>
                    <li>
                      <strong>Safari:</strong>{" "}
                      <a
                        href="https://support.apple.com/en-us/HT201265"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1a73e8" }}
                      >
                        Clear the history, cache, and cookies from Safari on your iPhone, iPad, or
                        iPod touch - Apple Support
                      </a>
                    </li>
                    <li>
                      <strong>Chrome:</strong>{" "}
                      <a
                        href="https://support.google.com/accounts/answer/32050"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1a73e8" }}
                      >
                        Clear cache & cookies - Android - Google Account Help
                      </a>
                    </li>
                    <li>
                      <strong>Edge:</strong>{" "}
                      <a
                        href="https://support.microsoft.com/en-us/microsoft-edge/manage-cookies-in-microsoft-edge-view-allow-block-delete-and-use-47cf2ff5-42e6-4c7a-b5e9-b5c4b6f9c8d8"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1a73e8" }}
                      >
                        Manage cookies in Microsoft Edge: View, allow, block, delete and use -
                        Microsoft Support
                      </a>
                    </li>
                    <li>
                      <strong>Samsung Internet:</strong>{" "}
                      <a
                        href="https://www.samsung.com/uk/support/mobile-devices/samsung-internet-browser-app-how-to-clear-cache-cookies-history/"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: "#1a73e8" }}
                      >
                        Samsung Internet browser app - how to clear cache, cookies & history |
                        Samsung UK
                      </a>
                    </li>
                  </ul>
                </div>
              </>
            )}
          </div>
        </UnauthenticatedTemplate>
        <Footer />
      </Suspense>
    </>
  );
};

export default UserProfile;
